import React, { useCallback, useRef, useState } from "react";

/**
 * value:
 *  - si multiple === false :
 *      soit null/undefined
 *      soit { id: "/media/123", name: "rapport_final.pdf" }
 *  - si multiple === true :
 *      tableau de { id, name }
 *
 * onUploaded(payload) est appelé :
 *  - single  => payload = { id, name }   OU null (quand on supprime)
 *  - multi   => payload = [ { id, name }, ... ] (liste complète après ajout/suppression)
 */
export default function UploadPdfDropzone({
  label,
  hint,
  maxMB = 100,
  multiple = false,
  required = false,
  value,
  onUploaded,
  apiBase,
  authHeaders,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  // normaliser la valeur entrante en tableau interne pour l'affichage
  const currentFiles = multiple
    ? Array.isArray(value) ? value : []
    : value ? [value] : [];

  const bytesToMB = (b) => Math.round((b / (1024 * 1024)) * 10) / 10;
  const isPdf = (f) => f && f.type === "application/pdf";

  /**
   * Upload un fichier unique
   * renvoie { id, name }
   * -> id vient du backend (json.id)
   * -> name est le nom local du fichier f.name
   */
  const uploadMedia = (file) =>
    new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append("file", file, file.name);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${apiBase}/media`, true);

      const headers = authHeaders ? authHeaders() : {};
      Object.keys(headers || {}).forEach((k) => {
        if (k.toLowerCase() !== "content-type") {
          xhr.setRequestHeader(k, headers[k]);
        }
      });

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          setProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const json = JSON.parse(xhr.responseText || "{}");
            const id = json?.id;
            if (!id) return reject(new Error("L'API n'a pas renvoyé d'id de fichier"));
            resolve({ id, name: file.name });
          } catch {
            reject(new Error("Réponse d'API invalide"));
          }
        } else {
          reject(new Error(`Upload échoué (HTTP ${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error("Erreur réseau pendant l'upload"));
      xhr.send(fd);
    });

  const validateFiles = (files) => {
    if (!files.length) return "Aucun fichier sélectionné.";
    for (const f of files) {
      if (!isPdf(f)) return "Seuls les PDF sont acceptés.";
      if (bytesToMB(f.size) > maxMB) return `Un fichier dépasse ${maxMB} MB.`;
    }
    return "";
  };

  /**
   * handleFiles:
   * - upload chaque fichier
   * - construit la nouvelle valeur "courante" pour le parent
   *   * single  -> dernier fichier uploadé remplace l'ancien
   *   * multi   -> concat à la liste existante
   */
  const handleFiles = async (files) => {
    setError("");
    const err = validateFiles(files);
    if (err) {
        setError(err);
        return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const uploaded = [];
      for (const f of files) {
        const fileObj = await uploadMedia(f); // {id,name}
        uploaded.push(fileObj);
      }

      if (multiple) {
        // concat liste existante + nouveaux
        const nextList = [...currentFiles, ...uploaded];
        onUploaded(nextList);
      } else {
        // un seul fichier : on remplace
        onUploaded(uploaded[0] || null);
      }
    } catch (e) {
      setError(e.message || "Échec de téléversement.");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 400);
    }
  };

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      const files = Array.from(e.dataTransfer.files || []);
      if (!files.length) return;

      handleFiles(multiple ? files : [files[0]]);
    },
    [multiple, currentFiles] // currentFiles pour que handleFiles ait la bonne closure
  );

  const onChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    handleFiles(multiple ? files : [files[0]]);
    e.target.value = ""; // reset input pour pouvoir re-sélectionner le même nom
  };

  /**
   * removeAt:
   * - enlève un fichier visuellement
   * - et avertit le parent de la nouvelle "valeur"
   *   * multi  -> nouvelle liste sans l'index
   *   * single -> null
   *
   * NOTE IMPORTANTE :
   * Avant tu renvoyais soit "" soit un tableau de strings. Le parent essayait
   * ensuite de deviner si c'était "la liste complète" ou "de nouveaux fichiers"
   * et refaisait du spread [...]. C'est ça qui causait l'ajout inattendu.
   *
   * Maintenant on renvoie DIRECTEMENT l'état final. Le parent n'a plus à faire
   * de logique conditionnelle compliquée.
   */
  const removeAt = (idx, e) => {
    e.stopPropagation(); // évite d'ouvrir le sélecteur
    const nextList = currentFiles.filter((_, i) => i !== idx);

    if (multiple) {
      onUploaded(nextList); // liste complète MAJ
    } else {
      onUploaded(null); // plus de fichier
    }
  };

  return (
    <div className="upload-block">
      <span className="upload-label">
        {label}
        {required && " *"}
      </span>

      <div
        className={`upload-dropzone ${dragOver ? "is-over" : ""} ${uploading ? "is-uploading" : ""}`}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple={multiple}
          required={required && currentFiles.length === 0}
          onChange={onChange}
          style={{ display: "none" }}
        />

        {/* --- CONTENU DU CADRE --- */}
        {currentFiles.length === 0 ? (
          <div className="upload-empty">
            <div className="upload-icon" aria-hidden>
              <svg width="36" height="36" viewBox="0 0 24 24">
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  fill="none"
                  stroke="currentColor"
                />
                <path d="M14 2v6h6" fill="none" stroke="currentColor" />
              </svg>
            </div>
            <div className="upload-text">
              <strong>Déposez le fichier ici</strong>
              <span>ou cliquez pour parcourir</span>
              {hint && <small className="upload-hint-inline">{hint}</small>}
            </div>
          </div>
        ) : (
          <div className={`upload-grid ${multiple ? "multi" : "single"}`}>
            {currentFiles.map((fileObj, i) => (
              <div
                key={`${fileObj.id}-${i}`}
                className="pdf-tile"
                title={fileObj.name} // <- tooltip = vrai nom
              >
                <div className="pdf-icon" aria-hidden>
                  {/* Icône PDF */}
                  <svg width="44" height="44" viewBox="0 0 24 24">
                    <path
                      d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
                      fill="none"
                      stroke="currentColor"
                    />
                    <path d="M14 2v6h6" fill="none" stroke="currentColor" />
                    <text x="6.5" y="18" fontSize="5" fontFamily="sans-serif">PDF</text>
                  </svg>
                </div>

                {/* Bouton retirer */}
                <button
                  type="button"
                  className="tile-remove"
                  aria-label={`Retirer ${fileObj.name}`}
                  onClick={(e) => removeAt(i, e)}
                >
                  ×
                </button>
                
                {/* Nom réel du fichier sous l'icône */}
                <div className="pdf-filename">
                  {fileObj.name}
                </div>

              </div>
            ))}

            {multiple && (
              <button
                type="button"
                className="pdf-tile add-more"
                aria-label="Ajouter d'autres fichiers"
              >
                +
              </button>
            )}
          </div>
        )}

        {uploading && (
          <div className="upload-progress">
            <div className="upload-bar" style={{ width: `${progress}%` }} />
            <span>{progress}%</span>
          </div>
        )}
      </div>

      {error && <div className="upload-error">{error}</div>}
    </div>
  );
}
